<script lang="ts" setup>
  import { useQuery } from '@pinia/colada';
  import { refDebounced } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';
  import { reasonOf } from '../../../../shared/api/client.ts';
  import type { PlayersGuessingView } from '../../../../shared/api/round.ts';
  import { guessSong as postGuessedSong, type Song } from '../../../../shared/api/song.ts';
  import { suggestionsQuery } from '../../../../shared/api/suggestions.ts';
  import { startUserAction, userAction } from '../../../../shared/observability/userAction.ts';
  import GuesserGuessingView from './GuesserGuessingView.vue';

  const props = defineProps<{ data: PlayersGuessingView; secondsLeft: string; isHost: boolean }>();
  const emit = defineEmits<{ guessedSong: [] }>();

  const songQuery = ref<string>('');
  const search = refDebounced(songQuery, 200);
  const { data: suggested, error: searchFailure } = useQuery(() => suggestionsQuery(search.value));
  const suggestions = computed(() => suggested.value ?? []);

  const isGuessingLoading = ref(false);

  const guessSong = async (song: Song) => {
    if (isGuessingLoading.value) return;

    startUserAction(userAction.roundGuessSong, suggestionPick(song));
    isGuessingLoading.value = true;
    try {
      await postGuessedSong(song);
      emit('guessedSong');
    } catch (error) {
      err.value = reasonOf(error);
    } finally {
      isGuessingLoading.value = false;
    }
  };

  const err = ref<string>('');
  const searchErr = ref<string>('');

  const suggestionPick = (song: Song) => ({
    suggestion_count: suggestions.value.length,
    suggestion_index: suggestions.value.indexOf(song),
    round: props.data.round.number,
  });

  watch(songQuery, () => {
    err.value = '';
    searchErr.value = '';
  });

  watch(searchFailure, (failure) => {
    if (failure) searchErr.value = reasonOf(failure);
  });
</script>

<template>
  <GuesserGuessingView
    v-model="songQuery"
    :data="data"
    :error="err"
    :is-host="isHost"
    :search-error="searchErr"
    :is-loading="isGuessingLoading"
    :songs="suggestions"
    :seconds-left="secondsLeft"
    @guessed-song="guessSong"
  />
</template>
